/**
 * FB54 — the job page's top tab row, computed.
 *
 * A restoration file is rarely one job. `26-1273-WTR` (mitigation) grows
 * `26-1273-MLD` (mold) and `26-1273-REC` (the rebuild), each its own `jobs` row
 * sharing the `YY-NNNN` base — the same family convention `familyBaseOf`
 * (gantt-model) and `jobFamilyBase` (accounting-model) already use.
 *
 * The row used to be five fixed tabs. It now reads:
 *
 *   1 Customer Info · 2 Job Info · 3 Dates · 4 Insurance · 5 Accounting
 *   ‖ 1 Mold Job Info · 2 Mold Accounting
 *   ‖ 1 Rebuild Job Info · 2 Rebuild Accounting
 *
 * Two rules the owner set, both encoded here rather than in the component:
 *
 *   • Each associated division contributes exactly TWO tabs — its job
 *     information and its accounting — and the numbering RESTARTS inside the
 *     group, so a group reads as its own numbered pair rather than as tabs 6
 *     and 7 of a run of eleven.
 *   • A job with no siblings produces exactly the five tabs it always had:
 *     no divider, no extra chrome, nothing to explain.
 *
 * Everything here is pure (no React, no supabase, no DOM) so the list can be
 * asserted directly — JobTopTabs.tsx only paints what this returns.
 */

import { DIVISIONS, type DivisionCode } from '~/lib/format';

/**
 * What a division tab is titled with. `DIVISION_LABELS` is too long for a row
 * that has to hold eleven tabs ("Contents Packout/Packback Accounting"), so
 * each code gets the one word the office actually says.
 */
export const DIVISION_TAB_LABELS: Record<DivisionCode, string> = {
  WTR: 'Water',
  MLD: 'Mold',
  REC: 'Rebuild',
  CON: 'Contents',
  CPO: 'Packout',
  STC: 'Structural',
  ENV: 'Environmental',
  ASB: 'Asbestos',
  TRM: 'Trauma',
  TMP: 'Temp Repairs',
  REF: 'Referral',
};

/** `MLD` → `Mold`. An unknown code falls back to the code itself. */
export function divisionTabLabel(code: string | null | undefined): string {
  const div = (code ?? '').trim().toUpperCase();
  return DIVISION_TAB_LABELS[div as DivisionCode] ?? (div || 'Division');
}

/**
 * Where a division sorts in the row: the repo's canonical `DIVISIONS` order
 * (water → mold → rebuild → contents → …), unknown codes last.
 */
export function divisionRank(code: string | null | undefined): number {
  const i = DIVISIONS.indexOf((code ?? '').trim().toUpperCase() as DivisionCode);
  return i === -1 ? DIVISIONS.length : i;
}

/** The things a top tab can show. Division tabs reuse `job` / `accounting`. */
export type TopTabKind = 'customer' | 'billing' | 'job' | 'dates' | 'insurance' | 'accounting';

export interface SiblingRef {
  jobNumber: string;
  division: string | null;
}

export interface TopTab {
  /**
   * Addressable tab id. Primary tabs keep their historic plain keys
   * (`customer`, `job`, …); a division tab is `<kind>:<jobNumber>`, e.g.
   * `job-info:26-1273-MLD` / `accounting:26-1273-MLD`.
   */
  key: string;
  kind: TopTabKind;
  /** Number shown in the pill. Restarts at 1 in every division group. */
  n?: number;
  label: string;
  /** Group index: 0 is the primary job, 1..n the associated divisions. */
  group: number;
  /** First tab of a division group — the row draws a break before it. */
  startsGroup?: boolean;
  /** Set on division tabs: whose job this tab reads and writes. */
  jobNumber?: string;
  division?: string;
  /** Eligible for the SELF PAY badge when the job is self pay (FB7). */
  badge?: boolean;
}

/** `job-info:26-1273-MLD` → `26-1273-MLD`; a primary key → null. */
export function tabJobNumber(key: string): string | null {
  const i = key.indexOf(':');
  return i === -1 ? null : key.slice(i + 1) || null;
}

export function divisionTabKey(kind: 'job' | 'accounting', jobNumber: string): string {
  return `${kind === 'job' ? 'job-info' : 'accounting'}:${jobNumber}`;
}

/**
 * De-duplicate and order the associated jobs: canonical division order first,
 * job number as the tie-break so the row is stable across reloads.
 */
export function orderSiblings(siblings: readonly SiblingRef[]): SiblingRef[] {
  const seen = new Set<string>();
  const out: SiblingRef[] = [];
  for (const s of siblings) {
    const jn = (s.jobNumber ?? '').trim();
    if (!jn || seen.has(jn)) continue;
    seen.add(jn);
    out.push({ jobNumber: jn, division: s.division });
  }
  return out.sort((a, b) => {
    const d = divisionRank(a.division) - divisionRank(b.division);
    return d !== 0 ? d : a.jobNumber.localeCompare(b.jobNumber);
  });
}

export interface BuildTopTabsOptions {
  /** Associated jobs (siblings only — never the job being viewed). */
  siblings?: readonly SiblingRef[];
  /** Billing Information sits between Customer and Job Info when present. */
  billingVisible?: boolean;
}

/**
 * The whole row, in paint order.
 *
 * Five primary tabs (six with Billing), then two per associated division. The
 * Billing tab is deliberately unnumbered — it always was, because it is an
 * offshoot of Customer Information rather than a step of its own.
 */
export function buildTopTabs(
  { siblings = [], billingVisible = false }: BuildTopTabsOptions = {},
): TopTab[] {
  const tabs: TopTab[] = [
    { key: 'customer', kind: 'customer', n: 1, label: 'Customer Info', group: 0, badge: true },
  ];
  if (billingVisible) {
    tabs.push({ key: 'billing', kind: 'billing', label: 'Billing Information', group: 0 });
  }
  tabs.push(
    { key: 'job', kind: 'job', n: 2, label: 'Job Info', group: 0 },
    { key: 'dates', kind: 'dates', n: 3, label: 'Dates', group: 0 },
    // Accounting last per Tim (8/1) — row reads Customer · Job · Dates · Insurance · Accounting.
    { key: 'insurance', kind: 'insurance', n: 4, label: 'Insurance', group: 0, badge: true },
    { key: 'accounting', kind: 'accounting', n: 5, label: 'Accounting', group: 0, badge: true },
  );

  orderSiblings(siblings).forEach((s, i) => {
    const name = divisionTabLabel(s.division);
    const division = (s.division ?? '').trim().toUpperCase() || undefined;
    tabs.push(
      {
        key: divisionTabKey('job', s.jobNumber),
        kind: 'job',
        n: 1,
        label: `${name} Job Info`,
        group: i + 1,
        startsGroup: true,
        jobNumber: s.jobNumber,
        division,
      },
      {
        key: divisionTabKey('accounting', s.jobNumber),
        kind: 'accounting',
        n: 2,
        label: `${name} Accounting`,
        group: i + 1,
        jobNumber: s.jobNumber,
        division,
      },
    );
  });

  return tabs;
}
